import type {
    DexQueryView,
    DexResultsSummaryView,
    DexResultsView,
    DexResultView,
    DexQueryAndResultsView,
} from '@mark43/dex-api';
import { DexDispatcherFormEnumType } from '@mark43/cad-api';
import { some } from 'lodash';
import { action, computed, makeObservable, observable } from 'mobx';
import { DexQueryAndResultView, DexQueryAndSummaryView } from '../types';
import type { FrDexPageViewMode } from '../types';
import { isHit, isHotHit } from '../utils';
import { KeyboardControlledDexResultsStore } from './use-dex-results-keyboard-navigation';
import {
    EventWorkspacePanelDexFormPrefill,
    EventWorkspacePanelDexNextFormPrefill,
} from '../../panels/workspace-panel-types';
import { resultsHaveHit } from './utils';

export class DexResultsStore implements KeyboardControlledDexResultsStore {
    @observable
    private data: {
        dexResultsViews: DexResultsView[] | undefined;
        latestSeenDexIncomingMessageId: number | undefined;
    };

    /**
     * dex query may return multiple dexResultsView (DexResultMenuListItem) for each entity (person, vehicle)
     * For each 'queryEntity', there may be a 'summary', followed by multiple 'results' in the dex results.
     * 'selectedViewFlatIndex' to track the index of the 'dexResultsView' ('result' or 'summary') across all DexResultMenuListItems,
     * keyboard shortcut navigates to prev/next entity or 'dexResultsView' based on this index.
     */
    @observable
    private selectedViewFlatIndex: number | undefined;
    /**
     * each query may return multiple 'results' from different sources, e.g. rms, ncic, dmv.
     * each result has a unique 'incomingMessageId'.
     * NOTE: 'summary' doesn't have 'incomingMessageId'
     */
    @observable
    private selectedResultId: number | undefined; // use incomingMessageId as id.
    /**
     * dex result may contain at most one 'summary' for searched entity, it's a summary of all 'results' from different sources,
     * we use 'outgoingMessageId' as the id for this summary record.
     */
    @observable
    private selectedSummaryId: number | undefined; // use outgoingMessageId as id.

    @observable
    private frPageViewMode: FrDexPageViewMode;
    private actingUserId;
    @observable
    private frPrefillValues: EventWorkspacePanelDexFormPrefill | undefined;
    @observable
    private frNextPrefillValues: EventWorkspacePanelDexNextFormPrefill | undefined;
    @observable
    private initialFrFormType: DexDispatcherFormEnumType | undefined;

    constructor(actingUserId: number, latestSeenDexIncomingMessageId: number | undefined) {
        this.data = { dexResultsViews: undefined, latestSeenDexIncomingMessageId };
        this.selectedViewFlatIndex = undefined;
        this.selectedResultId = undefined;
        this.selectedSummaryId = undefined;
        this.frPageViewMode = 'RESULTS';
        this.actingUserId = actingUserId;
        this.frPrefillValues = undefined;
        this.frNextPrefillValues = undefined;
        this.initialFrFormType = undefined;
        makeObservable(this);
    }

    @action
    setDexResultsViews(
        dexResultsViews: DexResultsView[] | undefined,
        isFirstResponder: boolean
    ): void {
        this.data.dexResultsViews = this.filterOutNonHitFromOtherUsers(
            isFirstResponder,
            dexResultsViews
        );
    }

    @action
    upsertDexResultsViews(
        dexResultsViews: DexResultsView[],
        isFirstResponder: boolean
    ): {
        hasNewHotHitFromOtherUserQuery: boolean;
        hasNewHotHitFromOwnQuery: boolean;
        outgoingMessageIdsWithNewSummaryViewFromOwnQuery: number[];
    } {
        let anyHasNewHotHitFromOtherUserQuery = false;
        let anyHasNewHotHitFromOwnQuery = false;
        const outgoingMessageIdsWithNewSummaryViewFromOwnQuery: number[] = [];
        this.filterOutNonHitFromOtherUsers(isFirstResponder, dexResultsViews)?.forEach(
            (dexResultsView) => {
                const { hasNewHotHit, hasNewSummaryView, isOwnQuery } =
                    this.upsertDexResultsView(dexResultsView);

                if (hasNewHotHit && !isOwnQuery) {
                    anyHasNewHotHitFromOtherUserQuery = true;
                }
                if (hasNewHotHit && isOwnQuery) {
                    anyHasNewHotHitFromOwnQuery = true;
                }
                if (hasNewSummaryView && isOwnQuery) {
                    outgoingMessageIdsWithNewSummaryViewFromOwnQuery.push(
                        dexResultsView.query.outgoingMessageId
                    );
                }
            }
        );
        return {
            hasNewHotHitFromOtherUserQuery: anyHasNewHotHitFromOtherUserQuery,
            hasNewHotHitFromOwnQuery: anyHasNewHotHitFromOwnQuery,
            outgoingMessageIdsWithNewSummaryViewFromOwnQuery,
        };
    }

    @action
    setSelectedResultId(incomingMessageId: number | undefined): void {
        this.selectedResultId = incomingMessageId;
        if (incomingMessageId) {
            this.selectedSummaryId = undefined;
        }
    }

    @action
    setSelectedSummaryId(outgoingMessageId: number | undefined) {
        this.selectedSummaryId = outgoingMessageId;
        if (outgoingMessageId) {
            this.selectedResultId = undefined;
        }
    }

    @action
    moveToPreviousQueryEntity(): void {
        this.moveToQueryEntity('prev');
    }

    @action
    moveToNextQueryEntity(): void {
        this.moveToQueryEntity('next');
    }

    @action
    moveToPreviousSummaryOrResult(): void {
        this.moveToSummaryOrResult('prev');
    }

    @action
    moveToNextSummaryOrResult(): void {
        this.moveToSummaryOrResult('next');
    }

    @action
    getSelectedResultId(): number | undefined {
        return this.selectedResultId;
    }

    @action
    getSelectedSummaryId(): number | undefined {
        return this.selectedSummaryId;
    }

    @computed
    get selectedQueryAndResult(): DexQueryAndResultView | undefined {
        if (this.selectedResultId) {
            const result = this.getMatchingResult(this.selectedResultId);
            if (result) {
                const dexQueryEntity = this.getMatchingDexQueryEntity(result.outgoingMessageId);
                if (dexQueryEntity?.query) {
                    return { query: dexQueryEntity.query, result };
                }
            }
        }
    }

    @computed
    get selectedQueryAndSummary(): DexQueryAndSummaryView | undefined {
        if (this.selectedSummaryId) {
            const dexQueryEntity = this.getMatchingDexQueryEntity(this.selectedSummaryId);
            if (
                dexQueryEntity &&
                'resultsSummaryView' in dexQueryEntity &&
                dexQueryEntity.resultsSummaryView
            ) {
                return { query: dexQueryEntity.query, summary: dexQueryEntity.resultsSummaryView };
            }
        }
    }

    @computed
    get resultCount(): number {
        return this.data.dexResultsViews ? this.data.dexResultsViews.length : 0;
    }

    @computed
    get notificationCount(): number {
        return this.notificationOutgoingMessageIds.length;
    }

    @computed
    get isNotificationUrgent(): boolean {
        return some(this.data.dexResultsViews, (resultsView) => this.hasUnseenHit(resultsView));
    }

    @action
    readResult(incomingMessageId: number): void {
        const result = this.getMatchingResult(incomingMessageId);
        if (result) {
            result.isRead = true;
        }
    }

    @action
    readSummary(outgoingMessageId: number): void {
        const summary = this.getMatchingSummary(outgoingMessageId);
        if (summary) {
            summary.isRead = true;
        }
    }

    @action
    setLatestSeenDexIncomingMessageId(latestSeenDexIncomingMessageId: number) {
        this.data.latestSeenDexIncomingMessageId = latestSeenDexIncomingMessageId;
    }

    @action
    setFrDexPageViewMode(mode: FrDexPageViewMode) {
        this.frPageViewMode = mode;
    }

    @action
    setFrPrefillValues(frPrefillValues: EventWorkspacePanelDexFormPrefill | undefined) {
        this.frPrefillValues = frPrefillValues;
    }

    @action
    setFrNextPrefillValues(frNextPrefillValues: EventWorkspacePanelDexNextFormPrefill | undefined) {
        this.frNextPrefillValues = frNextPrefillValues;
    }

    @action
    setInitialFrFormType(initialFrFormType: DexDispatcherFormEnumType | undefined) {
        this.initialFrFormType = initialFrFormType;
    }

    @computed
    get dexResultsViews(): DexResultsView[] {
        return this.data.dexResultsViews ?? [];
    }

    @computed
    get dexQueryEntityByOutgoingIdMap(): Record<
        number,
        DexResultsView | DexQueryAndResultsView | undefined
    > {
        // cascadedQueries (DexQueryAndResultsView[]) is an array field in DexResultsView. Flat all DexResultsView and DexQueryAndResultsView, keyed by outgoingMessageId (each Person/Vehicle query has a distinct outgoingMessageId)
        return (this.data.dexResultsViews ?? [])?.reduce<
            Record<number, DexResultsView | DexQueryAndResultsView | undefined>
        >((acc, cur) => {
            acc[cur.query.outgoingMessageId] = cur;
            if (cur.cascadedQueries?.length) {
                cur.cascadedQueries.forEach((cascadedQuery) => {
                    acc[cascadedQuery.query.outgoingMessageId] = cascadedQuery;
                });
            }
            return acc;
        }, {});
    }

    @computed
    get latestStoredDexMessageId(): number | undefined {
        const messageIds = [
            this.latestStoredDexOutgoingMessageId,
            this.latestStoredDexIncomingMessageId,
        ].filter((id) => !!id) as number[];
        return messageIds.length > 0 ? Math.max(...messageIds) : undefined;
    }

    @computed
    get latestStoredDexIncomingMessageId(): number | undefined {
        const incomingMessageIds = this.dexResultsViews
            .map((dexResultsView) =>
                dexResultsView.results.map((dexResultView) => dexResultView.incomingMessageId)
            )
            .flat();
        return incomingMessageIds.length > 0 ? Math.max(...incomingMessageIds) : undefined;
    }

    @computed
    get frDexPageViewMode(): FrDexPageViewMode {
        return this.frPageViewMode;
    }

    @computed
    get frDexPrefillValues(): EventWorkspacePanelDexFormPrefill | undefined {
        return this.frPrefillValues;
    }

    @computed
    get frDexNextPrefillValues(): EventWorkspacePanelDexNextFormPrefill | undefined {
        return this.frNextPrefillValues;
    }

    @computed
    get initialFrDexFormType(): DexDispatcherFormEnumType | undefined {
        return this.initialFrFormType;
    }

    @computed
    private get latestStoredDexOutgoingMessageId(): number | undefined {
        const outgoingMessageIds = this.dexResultsViews.map(
            (dexResultsView) => dexResultsView.query.outgoingMessageId
        );
        return outgoingMessageIds.length > 0 ? Math.max(...outgoingMessageIds) : undefined;
    }

    @action
    private upsertDexResultsView(dexResultsView: DexResultsView): {
        hasNewHotHit: boolean;
        hasNewSummaryView: boolean;
        isOwnQuery: boolean;
    } {
        const hasNewHotHit = this.hasNewHotHit(
            dexResultsView,
            this.getExistingResults(dexResultsView)
        );
        const isOwnQuery = this.isOwnQuery(dexResultsView.query);
        const hasNewSummaryView = this.hasNewSummaryView(dexResultsView);
        if (!this.data.dexResultsViews) {
            this.data.dexResultsViews = [dexResultsView];
        }
        const matchingResultsIndex = this.data.dexResultsViews.findIndex(
            (drv) => drv.query.outgoingMessageId === dexResultsView.query.outgoingMessageId
        );
        if (matchingResultsIndex !== -1) {
            this.data.dexResultsViews[matchingResultsIndex] = dexResultsView;
        } else {
            this.data.dexResultsViews.unshift(dexResultsView);
        }
        return {
            hasNewHotHit,
            hasNewSummaryView,
            isOwnQuery,
        };
    }

    @computed
    private get notificationOutgoingMessageIds(): number[] {
        return this.dexResultsViews
            .filter((resultsView) => this.hasUnseenResults(resultsView))
            .map((result) => result.query.outgoingMessageId);
    }

    private hasUnseenResults(dexResultsView: DexResultsView): boolean {
        return some(dexResultsView.results, (result) =>
            this.data.latestSeenDexIncomingMessageId
                ? result.incomingMessageId > this.data.latestSeenDexIncomingMessageId
                : true
        );
    }

    @computed
    private get allFlatViews(): (DexResultView | DexResultsSummaryView)[] {
        return this.dexResultsViews
            .map((dexResultsView) => {
                const allResultsFromDexResultView = [];
                if (dexResultsView.resultsSummaryView) {
                    allResultsFromDexResultView.push(dexResultsView.resultsSummaryView);
                }
                allResultsFromDexResultView.push(
                    this.flattenDexResultViews(dexResultsView.results)
                );
                if (dexResultsView.cascadedQueries?.length) {
                    dexResultsView.cascadedQueries.forEach((cascadedQuery) =>
                        allResultsFromDexResultView.push(
                            this.flattenDexResultViews(cascadedQuery.results)
                        )
                    );
                }
                return allResultsFromDexResultView;
            })
            .flat()
            .flat();
    }

    // ===================================================================================

    private flattenDexResultViews(dexResultViews: DexResultView[]): DexResultView[] {
        return dexResultViews
            .map((result) => [result, ...this.flattenDexResultViews(result.childDexResultViews)])
            .flat();
    }

    private moveToSummaryOrResult(direction: 'prev' | 'next'): void {
        const flatResultsCount = this.allFlatViews.length;
        if (flatResultsCount) {
            // find current flat index
            if (this.selectedResultId === undefined && this.selectedSummaryId === undefined) {
                this.selectedViewFlatIndex = 0;
            } else if (this.selectedSummaryId) {
                this.selectedViewFlatIndex = this.getMatchingSummaryFlatIndex(
                    this.selectedSummaryId
                );
            } else if (this.selectedResultId) {
                this.selectedViewFlatIndex = this.getMatchingResultFlatIndex(this.selectedResultId);
            }

            // find next flat index, if there is a summary or result already selected
            if (
                this.selectedViewFlatIndex !== undefined &&
                (this.selectedResultId !== undefined || this.selectedSummaryId !== undefined)
            ) {
                if (direction === 'next' && this.selectedViewFlatIndex < flatResultsCount - 1) {
                    this.selectedViewFlatIndex++;
                } else if (direction === 'prev' && this.selectedViewFlatIndex > 0) {
                    this.selectedViewFlatIndex--;
                }
            }

            // select the next view
            if (this.selectedViewFlatIndex !== undefined) {
                const nextResult = this.allFlatViews[this.selectedViewFlatIndex];
                if (nextResult) {
                    if ('incomingMessageId' in nextResult) {
                        if (nextResult.incomingMessageId) {
                            this.setSelectedResultId(nextResult.incomingMessageId);
                        }
                    } else if ('outgoingMessageId' in nextResult) {
                        if (nextResult.outgoingMessageId) {
                            this.setSelectedSummaryId(nextResult.outgoingMessageId);
                        }
                    }
                }
            }
        }
    }

    private moveToQueryEntity(direction: 'prev' | 'next'): void {
        const queryEntitiesCount = this.dexResultsViews.length;
        if (queryEntitiesCount) {
            const movingToQueryEntity = this.getQueryEntitySummaryIdOrFirstResultId(direction);
            if (movingToQueryEntity) {
                const { summaryId, resultId } = movingToQueryEntity;
                if (summaryId) {
                    this.setSelectedSummaryId(summaryId);
                } else if (resultId) {
                    this.setSelectedResultId(resultId);
                }
            }
        }
    }

    private getQueryEntitySummaryIdOrFirstResultId(
        direction: 'prev' | 'next'
    ): { summaryId?: number; resultId?: number } | undefined {
        const currentOutgoingMessageId =
            this.selectedSummaryId ?? this.selectedQueryAndResult?.query?.outgoingMessageId;

        const currentOutgoingMessageIndex = currentOutgoingMessageId
            ? this.dexResultsViews.findIndex(
                  (dexResultsView) =>
                      dexResultsView.query.outgoingMessageId === currentOutgoingMessageId
              )
            : -1;

        if (currentOutgoingMessageIndex < 0) {
            const firstDexResultView = this.dexResultsViews[0];
            const summaryId = firstDexResultView?.resultsSummaryView?.outgoingMessageId;
            return {
                summaryId,
                resultId: summaryId
                    ? undefined
                    : firstDexResultView?.results?.[0]?.incomingMessageId,
            };
        }

        const step = direction === 'next' ? 1 : -1;
        let movingDexResultViewIndex = currentOutgoingMessageIndex + step;

        // Skip query entities that have no results and no summary (e.g. still pending)
        while (
            movingDexResultViewIndex >= 0 &&
            movingDexResultViewIndex < this.dexResultsViews.length
        ) {
            const view = this.dexResultsViews[movingDexResultViewIndex];
            if (view.resultsSummaryView || view.results.length > 0) {
                break;
            }
            movingDexResultViewIndex += step;
        }

        if (
            movingDexResultViewIndex >= 0 &&
            movingDexResultViewIndex < this.dexResultsViews.length
        ) {
            const movingDexResultView = this.dexResultsViews[movingDexResultViewIndex];
            const summaryId = movingDexResultView?.resultsSummaryView?.outgoingMessageId;
            return {
                summaryId,
                resultId: summaryId
                    ? undefined
                    : movingDexResultView?.results?.[0]?.incomingMessageId,
            };
        }
        return undefined;
    }

    private getMatchingResult(incomingMessageId: number): DexResultView | undefined {
        return this.allFlatViews.find(
            (dexResultView) =>
                'incomingMessageId' in dexResultView &&
                dexResultView.incomingMessageId === incomingMessageId
        ) as DexResultView;
    }

    private getMatchingSummary(outgoingMessageId: number): DexResultsSummaryView | undefined {
        return this.dexResultsViews.find(
            (dexResultsView) => dexResultsView.query.outgoingMessageId == outgoingMessageId
        )?.resultsSummaryView;
    }

    private getMatchingResultFlatIndex(incomingMessageId: number): number {
        return this.allFlatViews.findIndex(
            (dexResultView) =>
                'incomingMessageId' in dexResultView &&
                dexResultView.incomingMessageId === incomingMessageId
        );
    }

    private getMatchingSummaryFlatIndex(outgoingMessageId: number): number {
        return this.allFlatViews.findIndex(
            (dexResultsView) =>
                'outgoingMessageId' in dexResultsView &&
                dexResultsView.outgoingMessageId === outgoingMessageId
        );
    }

    private getMatchingDexQueryEntity(
        outgoingMessageId: number
    ): DexResultsView | DexQueryAndResultsView | undefined {
        return this.dexQueryEntityByOutgoingIdMap[outgoingMessageId];
    }

    private hasUnseenHit(dexResultsView: DexResultsView): boolean {
        return some(dexResultsView.results, (result) => this.isUnseen(result) && isHit(result));
    }

    private isUnseen(dexResultView: DexResultView): boolean {
        return this.data.latestSeenDexIncomingMessageId
            ? dexResultView.incomingMessageId > this.data.latestSeenDexIncomingMessageId
            : true;
    }

    private hasNewHotHit(
        incomingResultsView: DexResultsView,
        existingResultsView?: DexResultsView
    ): boolean {
        if (!existingResultsView) {
            return incomingResultsView.results.some(isHotHit);
        }
        const incomingHotHits = this.getHotHits(incomingResultsView);
        const existingHotHits = this.getHotHits(existingResultsView);
        return incomingHotHits.length > existingHotHits.length;
    }

    private getHotHits(dexResultsView: DexResultsView): DexResultView[] {
        const results = dexResultsView.results;
        const cascadedResults =
            dexResultsView.cascadedQueries?.flatMap((cascadedQuery) => cascadedQuery.results) ?? [];
        const allResults = results.concat(cascadedResults);
        return this.getHotHitsFromResults(allResults);
    }

    private getHotHitsFromResults(dexResultViews: DexResultView[]): DexResultView[] {
        if (dexResultViews.length === 0) {
            return [];
        }
        const childDexResultViews = dexResultViews.flatMap((result) => result.childDexResultViews);
        return dexResultViews
            .filter((result) => isHotHit(result))
            .concat(this.getHotHitsFromResults(childDexResultViews));
    }

    private isOwnQuery(query: DexQueryView): boolean {
        return query.createdBy === this.actingUserId;
    }

    private hasNewSummaryView(dexResultsView: DexResultsView): boolean {
        if (!dexResultsView.resultsSummaryView) {
            return false;
        }
        const existingResultsView = this.getExistingResults(dexResultsView);
        return !existingResultsView?.resultsSummaryView;
    }

    private getExistingResults(incomingDexResultsView: DexResultsView): DexResultsView | undefined {
        return this.dexResultsViews.find(
            (drv) => drv.query.outgoingMessageId === incomingDexResultsView.query.outgoingMessageId
        );
    }

    private filterOutNonHitFromOtherUsers(
        isFirstResponder: boolean,
        dexResultsViews?: DexResultsView[]
    ) {
        if (isFirstResponder) {
            return dexResultsViews;
        }
        return dexResultsViews?.filter(
            (d) => this.isOwnQuery(d.query) || resultsHaveHit(d?.results)
        );
    }
}
